import { Link, useNavigate } from "react-router-dom";
import HeroBannerV2 from "@/components/HeroBannerV2";
import Categories from "@/components/Categories";
import { useHeroBannerV2Public } from "@/hooks/useHeroBannerV2";
import { useMenuCategories } from "@/hooks/useCategories";
import { ChevronRight, Menu } from "lucide-react";
import { ArrowRight, ShieldCheck, Truck, HeartHandshake, BadgeCheck, ShoppingBag, Zap } from "lucide-react";
import { useDesignProducts } from "@/hooks/useDesignProducts";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useQuickBuy } from "@/contexts/QuickBuyContext";
import type { Product } from "@/components/ProductCard";
import { getDefaultSizeOfferPrice, getSelectedSizeRegularPrice } from "@/lib/productPricing";
import SectionHeader from "./SectionHeader";
import ProductRow from "./ProductRow";
import BabyCareTipsSection from "./BabyCareTipsSection";
import heroImage from "@/assets/baby-hero.jpg";
import toysImage from "@/assets/baby-toys.jpg";
import feedingImage from "@/assets/baby-feeding.jpg";
import diapersImage from "@/assets/baby-diapers.jpg";
import careImage from "@/assets/baby-care.jpg";
import clothingImage from "@/assets/baby-clothing.jpg";
import accessoriesImage from "@/assets/baby-accessories.jpg";
import newbornImage from "@/assets/baby-newborn.jpg";

const fallbackProducts = [
  { id: "look-toys", name: "Soft Plush Teddy", price: 690, image: toysImage, seo_slug: "products" },
  { id: "look-feeding", name: "Baby Feeding Bottle Set", price: 850, image: feedingImage, seo_slug: "products" },
  { id: "look-diaper", name: "Premium Diapers Pack", price: 990, image: diapersImage, seo_slug: "products" },
  { id: "look-care", name: "Baby Care Lotion & Towel", price: 720, image: careImage, seo_slug: "products" },
  { id: "look-cloth", name: "Cotton Baby Onesie (3pcs)", price: 890, image: clothingImage, seo_slug: "products" },
  { id: "look-acc", name: "Knitted Cap, Bib & Mittens", price: 540, image: accessoriesImage, seo_slug: "products" },
  { id: "look-newborn", name: "Newborn Swaddle Gift Set", price: 1290, image: newbornImage, seo_slug: "products" },
  { id: "look-rattle", name: "Colorful Rattle Toy Set", price: 450, image: toysImage, seo_slug: "products" },
];

const babyCategories = [
  { name: "Baby Toys", bn: "Baby Toys", slug: "baby-toys", image: toysImage },
  { name: "Feeding Sets", bn: "Feeding Sets", slug: "feeding-sets", image: feedingImage },
  { name: "Pampers / Diapers", bn: "Diapers", slug: "pampers-diapers", image: diapersImage },
  { name: "Baby Care", bn: "Baby Care", slug: "baby-care", image: careImage },
  { name: "Baby Clothing", bn: "Baby Clothing", slug: "baby-clothing", image: clothingImage },
  { name: "Accessories", bn: "Accessories", slug: "baby-accessories", image: accessoriesImage },
  { name: "Newborn Essentials", bn: "Newborn Needs", slug: "newborn-essentials", image: newbornImage },
];

type ShowcaseProduct = {
  id: string;
  name: string;
  price: string | number;
  offer_price?: number | null;
  image?: string | null;
  seo_slug?: string | null;
} & Partial<Product>;

const ShowcaseCard = ({ product }: { product: ShowcaseProduct }) => {
  const navigate = useNavigate();
  const { openQuickBuy } = useQuickBuy();
  const href = String(product.id).startsWith("look-") ? "/products" : `/product/${product.seo_slug || product.id}`;
  const price = getDefaultSizeOfferPrice(product);
  const regularPrice = getSelectedSizeRegularPrice(product);
  const hasDiscount = regularPrice > 0 && price > 0 && price < regularPrice;
  const discountPercent = hasDiscount ? Math.round(((regularPrice - price) / regularPrice) * 100) : 0;
  return (
    <div onClick={() => navigate(href)} className="group cursor-pointer overflow-hidden rounded-2xl bg-card shadow-sm transition-shadow hover:shadow-md">
      <div className="relative aspect-[3/4] overflow-hidden rounded-t-2xl bg-secondary">
        <img src={product.image || "/placeholder.svg"} alt={product.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        {hasDiscount && (
          <span className="absolute top-2 left-2 z-20 rounded-full bg-destructive px-2 py-0.5 text-[10px] font-bold text-destructive-foreground shadow-sm md:text-[11px]">
            -{discountPercent}%
          </span>
        )}
        {(product as any).free_delivery && (
          <span className="absolute top-2 right-2 z-20 rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow-sm md:text-[11px] flex items-center gap-0.5" style={{ backgroundColor: "hsl(142 70% 38%)" }}>
            🚚 Free Delivery
          </span>
        )}
        <div className="absolute bottom-3 left-3 right-3 z-20 hidden translate-y-2 gap-2 opacity-0 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100 md:flex">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openQuickBuy(product, "buy"); }}
            className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full bg-primary px-3 text-[11px] font-semibold uppercase tracking-wider text-primary-foreground shadow-md"
          >
            <Zap size={13} /> Buy Now
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openQuickBuy(product, "cart"); }}
            className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-full border border-border bg-card px-3 text-[11px] font-semibold uppercase tracking-wider text-foreground shadow-md"
          >
            <ShoppingBag size={13} /> Add to Cart
          </button>
        </div>
      </div>
      <div className="p-3 text-center">
        <h3 className="font-body text-sm leading-snug text-foreground md:text-base">{product.name}</h3>
        <div className="mt-1 flex items-center justify-center gap-2">
          <p className="font-price text-sm font-semibold text-primary">৳{price.toLocaleString()}</p>
          {hasDiscount && (
            <p className="font-price text-xs text-muted-foreground line-through">৳{regularPrice.toLocaleString()}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5 px-2.5 pb-2.5 md:hidden">
        <button type="button" onClick={(e) => { e.stopPropagation(); openQuickBuy(product, "buy"); }} className="h-8 rounded-lg bg-primary text-[9px] font-semibold uppercase tracking-wider text-primary-foreground">Buy Now</button>
        <button type="button" onClick={(e) => { e.stopPropagation(); openQuickBuy(product, "cart"); }} className="h-8 rounded-lg border border-border bg-card text-[9px] font-semibold uppercase tracking-wider text-foreground">Cart</button>
      </div>
    </div>
  );
};

const Home = () => {
  const { content } = useSiteContent();
  const brandName = content.brand_name || "Lovira Kids";
  const { config: heroV2Config, loading: heroV2Loading } = useHeroBannerV2Public();
  const heroV2Active = Boolean(heroV2Config?.enabled && (heroV2Config?.slides?.length ?? 0) > 0);
  const { data: menuCategories = [] } = useMenuCategories();
  const { newProducts, bestProducts, allProducts } = useDesignProducts({
    designId: "home",
    newLimit: 7,
    bestLimit: 7,
    allLimit: 7,
  });

  const featuredItems = (newProducts.length ? newProducts : fallbackProducts.slice(0, 4)).slice(0, 4);
  const bestItems = (bestProducts.length ? bestProducts : fallbackProducts.slice(2, 6)).slice(0, 4);
  const collectionItems = (allProducts.length ? allProducts : fallbackProducts).slice(0, 4);
  const hasMoreFeatured = newProducts.length > 6;
  const hasMoreBest = bestProducts.length > 6;
  const hasMoreAll = allProducts.length > 6;

  return (
    <div className="bg-background text-foreground">
      {/* HERO */}
      {heroV2Loading ? (
        <section data-section-id="home" className="bg-background">
          <div className="w-full">
            <div className="aspect-[1920/800] w-full animate-pulse bg-muted" />
          </div>
        </section>
      ) : heroV2Active ? (
        <section data-section-id="home" className="bg-gradient-to-br from-[hsl(var(--baby-peach))] via-[hsl(var(--baby-cream))] to-[hsl(var(--baby-lavender))]">
          <div className="grid w-full items-stretch lg:grid-cols-[280px_1fr]">
            {/* Categories sidebar — premium, fixed-height frame */}
            <aside className="hidden h-full self-stretch lg:block">
              <div className="flex h-full max-h-full flex-col overflow-hidden rounded-2xl border border-[hsl(var(--gold)/0.25)] bg-card shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.18)] ring-1 ring-black/5">
                {/* Header — gradient + gold underline */}
                <div className="relative flex items-center gap-2.5 bg-primary px-5 py-4 text-primary-foreground">
                  <Menu size={17} strokeWidth={2.25} />
                  <h2 className="font-heading text-[13px] font-semibold uppercase tracking-[0.18em]">Our Categories</h2>
                  <span className="absolute inset-x-5 bottom-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--gold-light))] to-transparent" />
                </div>

                {/* Fixed-height list — extra items overflow into "View All" (no scroll, no resize) */}
                <ul className="min-h-0 flex-1 divide-y divide-border/60 overflow-hidden">
                  {menuCategories.slice(0, 7).map((cat) => (
                    <li key={cat.id} className="group/cat">
                      <Link
                        to={`/category/${cat.slug}`}
                        className="relative flex items-center justify-between px-5 py-[13px] text-[13px] font-medium text-foreground/85 transition-colors hover:bg-secondary hover:text-primary"
                      >
                        <span className="truncate tracking-wide">{cat.name}</span>
                        <ChevronRight size={15} className="text-muted-foreground/60 transition-all group-hover/cat:translate-x-0.5 group-hover/cat:text-[hsl(var(--gold))]" />
                      </Link>
                    </li>
                  ))}
                  {menuCategories.length === 0 && (
                    <li className="px-5 py-3 text-sm text-muted-foreground">No categories available</li>
                  )}
                </ul>

                {/* Premium View All footer */}
                <Link
                  to="/categories"
                  className="group/all relative flex items-center justify-between gap-2 border-t border-[hsl(var(--gold)/0.25)] bg-primary px-5 py-3.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-primary-foreground transition-all hover:bg-primary/90"
                >
                  <span>View All Categories</span>
                  <ChevronRight size={15} className="transition-transform group-hover/all:translate-x-1" />
                </Link>
              </div>
            </aside>
            {/* Hero banner */}
            <div className="min-w-0">
              <HeroBannerV2 initialConfig={heroV2Config} />
            </div>
          </div>
        </section>
      ) : (
        <section data-section-id="home" className="bg-gradient-to-br from-[hsl(var(--baby-pink))] via-background to-[hsl(var(--baby-mint))]">
          <div className="mx-auto grid max-w-[1800px] items-center gap-8 px-4 py-10 md:grid-cols-2 md:gap-12 md:px-8 md:py-16">
            <div className="text-center md:text-left">
              <span className="inline-block rounded-full bg-card px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary shadow-sm">
                100% Safe & Trusted
              </span>
              <h1 className="mt-5 font-heading text-4xl leading-tight text-foreground md:text-6xl">
                {brandName}
                <span className="block text-primary">For Your Little One</span>
              </h1>
              <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-muted-foreground md:mx-0">
                Baby toys, feeding sets, diapers, baby care, and all essential items for newborns — delivered right to your doorstep, at affordable prices.
              </p>
              <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row md:items-start md:justify-start">
                <Link to="/products" className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground shadow-md transition-transform hover:scale-105">
                  Shop Now <ArrowRight size={16} />
                </Link>
                <Link to="/products" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-7 py-3 text-sm font-semibold text-foreground hover:bg-secondary">
                  All Categories
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl bg-card shadow-xl">
                <img src={heroImage} alt={`${brandName} - Baby & Kids products`} width={1280} height={896} className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </section>
      )}

      <Categories />

      {/* FEATURED */}
      <section data-section-id="new_arrivals" className="bg-secondary py-12 md:py-20">
        <div className="mx-auto max-w-[1800px] px-3 md:px-6">
          <SectionHeader title="Featured Products" viewAllHref={hasMoreFeatured ? "/new-arrivals" : undefined} viewAllLabel="View All" />
          <ProductRow products={featuredItems} />
          {hasMoreFeatured && (
            <div className="mt-6 flex justify-center md:hidden">
              <Link to="/new-arrivals" className="whitespace-nowrap border-b border-primary pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground transition-colors hover:text-primary">
                View All
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* BEST SELLERS */}
      <section data-section-id="best_sellers" className="bg-background py-12 md:py-20">
        <div className="mx-auto max-w-[1800px] px-3 md:px-6">
          <SectionHeader title="Best Sellers" viewAllHref={hasMoreBest ? "/best-selling" : undefined} viewAllLabel="View All" />
          <ProductRow products={bestItems} />
          {hasMoreBest && (
            <div className="mt-6 flex justify-center md:hidden">
              <Link to="/best-selling" className="whitespace-nowrap border-b border-primary pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground transition-colors hover:text-primary">
                View All
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-gradient-to-br from-[hsl(var(--baby-cream))] to-[hsl(var(--baby-pink))] py-6 md:py-10">
        <div className="mx-auto max-w-[1800px] px-3 md:px-6">
          <div className="mb-4 text-center md:mb-6">
            <h2 className="font-heading text-lg text-foreground md:text-2xl">Why Choose {brandName}?</h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground md:text-sm">Your baby's safety and comfort are our priority</p>
          </div>
          <div className="grid grid-cols-4 gap-2 md:gap-4">
            {[
              { icon: ShieldCheck, title: "100% Safe", desc: "Safe & tested" },
              { icon: BadgeCheck, title: "Original", desc: "Top quality" },
              { icon: Truck, title: "Fast Delivery", desc: "All Bangladesh" },
              { icon: HeartHandshake, title: "Cash on Delivery", desc: "Pay on receive" },
            ].map((f, i) => (
              <div key={i} className="rounded-xl bg-card p-2 text-center shadow-sm md:p-4">
                <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary md:h-11 md:w-11">
                  <f.icon size={16} className="md:hidden" />
                  <f.icon size={20} className="hidden md:block" />
                </div>
                <h3 className="mt-1.5 text-[11px] font-semibold leading-tight text-foreground md:mt-2 md:text-sm">{f.title}</h3>
                <p className="mt-0.5 text-[9px] leading-tight text-muted-foreground md:text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ALL PRODUCTS */}
      <section data-section-id="all_collections" className="bg-background py-12 md:py-20">
        <div className="mx-auto max-w-[1800px] px-3 md:px-6">
          <SectionHeader title="All Products" viewAllHref={hasMoreAll ? "/products" : undefined} viewAllLabel="View All" />
          <ProductRow products={collectionItems} />
          {hasMoreAll && (
            <div className="mt-6 flex justify-center md:hidden">
              <Link to="/products" className="whitespace-nowrap border-b border-primary pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground transition-colors hover:text-primary">
                View All
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* BABY CARE TIPS — Articles (last section before footer) */}
      <BabyCareTipsSection />
    </div>
  );
};

export default Home;